import Storage from 'utils/localStorage/Storage';
import Actions from 'Actions';
import Constants from 'utils/localStorage/Constants';
import productCompare from 'reducers/productCompare';
import { MAX_PRODUCTS } from 'constants/productCompare';
import getProductComparison from 'services/api/productComparison/getProductComparison';
import Empty from 'constants/empty';
import localeUtils from 'utils/LanguageLocale';
import ProductCompareActionsEN from 'actions/locales/ProductCompareActions_en_US';
import ProductCompareActionsFR from 'actions/locales/ProductCompareActions_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(ProductCompareActionsEN, ProductCompareActionsFR);
const { ACTION_TYPES: TYPES } = productCompare;
const { COMPARE_MODE_KEY, COMPARE_PRODUCTS_KEY, COMPARE_MODAL_OPEN_KEY } = Constants;

/**
 * Private helper to update products in both IndexedDB and Redux
 */
function updateProducts(products) {
    return async dispatch => {
        await Storage.db.setItem(COMPARE_PRODUCTS_KEY, products);
        dispatch({
            type: TYPES.SET_SELECTED_PRODUCTS,
            payload: products
        });
    };
}

/**
 * Set compare mode enabled/disabled
 * Updates both Redux and IndexedDB
 */
function setCompareMode(isEnabled) {
    return async dispatch => {
        await Storage.db.setItem(COMPARE_MODE_KEY, isEnabled);

        dispatch({
            type: TYPES.SET_COMPARE_MODE,
            payload: isEnabled
        });

        // Clear products in IndexedDB when disabling
        if (!isEnabled) {
            await Storage.db.setItem(COMPARE_PRODUCTS_KEY, []);
        }
    };
}

/**
 * Toggle a product (add or remove)
 * Updates both Redux and IndexedDB
 */
function toggleProduct(product, isSelected) {
    return async (dispatch, getState) => {
        const currentProducts = getState().productCompare.selectedProducts;
        let updatedProducts;

        if (isSelected) {
            // Add product if not already selected and under limit
            if (currentProducts.length < MAX_PRODUCTS && !currentProducts.find(p => p.productId === product.productId)) {
                updatedProducts = [...currentProducts, product];
            } else {
                dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('maxProdsTitle'),
                        message: getText('maxProdsDesc'),
                        buttonText: getText('gotIt'),
                        showCloseButton: true
                    })
                );

                return; // No change
            }
        } else {
            // Remove product
            updatedProducts = currentProducts.filter(p => p.productId !== product.productId);
        }

        dispatch(updateProducts(updatedProducts));
    };
}

/**
 * Remove a specific product
 * Updates both Redux and IndexedDB
 */
function removeProduct(productId) {
    return async (dispatch, getState) => {
        const currentProducts = getState().productCompare.selectedProducts;
        const updatedProducts = currentProducts.filter(p => p.productId !== productId);

        dispatch(updateProducts(updatedProducts));
    };
}

/**
 * Clear all selected products
 * Updates both Redux and IndexedDB
 */
function clearProducts() {
    return dispatch => {
        dispatch(updateProducts([]));
    };
}

/**
 * Load compare state from IndexedDB on mount
 * Hydrates Redux from persisted state
 */
function loadCompareState() {
    return async dispatch => {
        const savedCompareMode = await Storage.db.getItem(COMPARE_MODE_KEY);
        const savedProducts = await Storage.db.getItem(COMPARE_PRODUCTS_KEY);

        if (savedCompareMode !== null) {
            dispatch({
                type: TYPES.SET_COMPARE_MODE,
                payload: savedCompareMode
            });
        }

        if (Array.isArray(savedProducts)) {
            dispatch({
                type: TYPES.SET_SELECTED_PRODUCTS,
                payload: savedProducts
            });
        }

        // Check if the user navigated back from PDP while the Compare Modal was open.
        const shouldReopenModal = Storage.local.getItem(COMPARE_MODAL_OPEN_KEY);

        if (shouldReopenModal) {
            Storage.local.removeItem(COMPARE_MODAL_OPEN_KEY);
            dispatch({
                type: TYPES.SET_COMPARE_MODAL_OPEN,
                payload: true
            });
        }
    };
}

/**
 * Clear the shouldReopenModal flag in Redux after the modal has been re-opened
 */
function clearCompareModalReopenFlag() {
    return {
        type: TYPES.SET_COMPARE_MODAL_OPEN,
        payload: false
    };
}

function clearComparisonData() {
    return {
        type: TYPES.SET_COMPARISON_DATA,
        payload: null
    };
}

export function fetchProductComparison(payload = {}) {
    return async (dispatch, getState) => {
        // Reset prior fetch outcome so a new open/retry does not see stale
        // comparisonData or comparisonError before this request finishes.
        dispatch({
            type: TYPES.SET_COMPARISON_DATA,
            payload: null
        });

        const user = getState().user;
        const preferredStore = user?.preferredStore || Empty.String;
        const preferredZipCode = user?.preferredZipCode || Empty.String;

        payload.preferredStore = preferredStore;
        payload.preferredZipCode = preferredZipCode;

        try {
            const products = await getProductComparison(payload);

            dispatch({
                type: TYPES.SET_COMPARISON_DATA,
                payload: products || []
            });

            return products || [];
        } catch (error) {
            dispatch({
                type: TYPES.SET_COMPARISON_ERROR,
                payload: error
            });
            throw error;
        }
    };
}

export default {
    setCompareMode,
    toggleProduct,
    removeProduct,
    clearProducts,
    loadCompareState,
    fetchProductComparison,
    clearCompareModalReopenFlag,
    clearComparisonData
};
